import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function UserRolesPage() {
  return (
    <DocsLayout
      title="User roles"
      updated="May 9, 2026"
      summary="Who can do what inside an organisation. Two roles by default — Admin and User — plus an implicit Owner that maps to whoever created the org."
    >
      <h2>The roles you'll see in the UI</h2>
      <p>
        Rynora's invite picker exposes two roles. We deliberately keep the picker simple — most teams need exactly these
        two and nothing more.
      </p>

      <h3>Admin</h3>
      <p>
        Admins can do everything inside the organisation: invite people, manage billing, create or delete projects,
        change plan, configure SSO, and request a project restore from Rynora support.
      </p>

      <h3>User</h3>
      <p>
        Users can build flows, run them, look at run history, manage variables, and participate in any project they're
        invited to. They can't invite new members, change billing, or delete the organisation.
      </p>

      <h2>The implicit Owner role</h2>
      <p>
        Whoever signs up and creates the organisation becomes its <strong>Owner</strong>. The Owner has the same
        capabilities as an Admin, plus one safeguard: <em>nobody can remove the Owner</em>, even another Admin. This
        prevents an admin from accidentally orphaning the org's billing relationship.
      </p>
      <p>
        Owners can transfer ownership to another Admin from <strong>Settings → Members</strong>. The previous Owner
        becomes a regular Admin afterwards.
      </p>

      <h2>Capability table</h2>
      <table>
        <thead>
          <tr>
            <th>Action</th>
            <th>User</th>
            <th>Admin</th>
            <th>Owner</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Build, edit, run flows</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Manage project variables &amp; secrets</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Invite new members</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Change member roles</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Create / soft-delete projects</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Permanently delete a project</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Manage billing &amp; plan</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Configure SSO</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Delete the organisation</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Be removed from org</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>—</td>
          </tr>
        </tbody>
      </table>

      <h2>What Rynora staff can see</h2>
      <p>
        Rynora support agents have a separate <strong>platform admin</strong> role that lives outside your organisation.
        They cannot read your data unless you ask them to, and any time they act on your org we record an audit row
        tagged <code>actor_role=platform_admin</code> with the support ticket linked. You can ask for a copy of those
        rows at any time — see <Link to="/docs/delete-and-restore">Delete &amp; restore</Link> for an example.
      </p>

      <h2>Two-factor &amp; SSO</h2>
      <p>
        Email/password accounts can enable TOTP from <strong>Profile → Security</strong>. Enterprise plans can disable
        password login entirely and require Google, Microsoft, or SAML SSO — see{' '}
        <Link to="/docs/api">API reference</Link> for the SSO endpoints.
      </p>

      <h2>Need a custom permission?</h2>
      <p>
        Tell us. We've kept the role list short on purpose, but if your team needs &quot;run-only&quot; or
        &quot;read-only&quot; viewers we'd like to hear about it before we add another role. Email{' '}
        <a href="mailto:support@rynora.app">support@rynora.app</a>.
      </p>
    </DocsLayout>
  );
}
